import { useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';
import { useNodeHighlighting } from './useNodeHighlighting';
import { useNodeSelection } from '@/features/connect-dots/hooks/useNodeSelection';
import { Node, NodeType, MIN_ZOOM, MAX_ZOOM } from '../types/cytoscape';

interface UseCytoscapeEventsProps {
  cytoscapeCoreRefObject: cytoscape.Core | null;
  showTooltip: (
    ref: React.RefObject<HTMLDivElement>,
    content: Node,
    position: { x: number; y: number }
  ) => void;
  hideTooltip: () => void;
  zoomLevel: number;
  setZoomLevel: (value: number) => void;
}

export const useCytoscapeEvents = ({
  cytoscapeCoreRefObject,
  showTooltip,
  hideTooltip,
  // zoomLevel,
  setZoomLevel,
}: UseCytoscapeEventsProps) => {
  const { highlightedNodes, highlightNodeAndConnections, removeHighlighting } =
    useNodeHighlighting();

  const { clearSelection } = useNodeSelection();

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // コールバック関数を useRef でラップ
  const handleClickRef = useRef<
    ((event: cytoscape.EventObject) => void) | null
  >(null);
  const handleMouseOverRef = useRef<
    ((event: cytoscape.EventObject) => void) | null
  >(null);
  const handleMouseOutRef = useRef<
    ((event: cytoscape.EventObject) => void) | null
  >(null);

  // コールバック関数を最新化
  useEffect(() => {
    const cy = cytoscapeCoreRefObject;
    if (!cy) return;

    handleClickRef.current = (event: cytoscape.EventObject) => {
      // ハイライト中は、ハイライトされているノード以外のクリックを無視
      if (highlightedNodes.length > 0) {
        if (event.target.isNode && event.target.isNode()) {
          if (!highlightedNodes.includes(event.target.id())) {
            removeHighlighting(cytoscapeCoreRefObject);
            return;
          }
        }
      }

      if (event.target === cy || event.target.isEdge()) {
        hideTooltip();
      } else if (event.target.isNode && event.target.isNode()) {
        if (highlightedNodes.length === 0) {
          removeHighlighting(cytoscapeCoreRefObject);
          const node: cytoscape.NodeSingular = event.target;
          highlightNodeAndConnections(cytoscapeCoreRefObject, node.id());
        }
        handleNodeToolTipShow(event);
      }
    };

    handleMouseOverRef.current = (event: cytoscape.EventObject) => {
      if (!event.target || !event.target.isNode || !event.target.isNode()) {
        return; // ノード以外の場合は何もしない
      }
      if (highlightedNodes.length > 0) {
        // ハイライト中は、ハイライトされているノードのみツールチップを表示
        if (
          event.target.isNode &&
          event.target.isNode() &&
          !highlightedNodes.includes(event.target.id())
        ) {
          return;
        }
        handleNodeToolTipShow(event);
      }
    };

    // Cytoscapeの外にマウスが出たらツールチップを非表示にする
    handleMouseOutRef.current = (event: cytoscape.EventObject) => {
      // マウスカーソルがCytoscapeの要素上にある場合は非表示にしない
      if (
        (event.target && event.target.isNode && event.target.isNode()) ||
        (event.target.isEdge && event.target.isEdge())
      ) {
        return; // Cytoscapeの要素上にある場合は何もしない
      }

      // ツールチップ上にマウスがある場合は非表示にしない
      if (tooltipRef.current) {
        const tooltipElement = tooltipRef.current;
        const rect = tooltipElement.getBoundingClientRect();
        const mouseX = event.originalEvent.clientX;
        const mouseY = event.originalEvent.clientY;

        if (
          mouseX >= rect.left &&
          mouseX <= rect.right &&
          mouseY >= rect.top &&
          mouseY <= rect.bottom
        ) {
          return; // ツールチップ上にマウスがある場合は何もしない
        }
      }

      hideTooltip();
    };
  }, [
    cytoscapeCoreRefObject,
    highlightNodeAndConnections,
    removeHighlighting,
    hideTooltip,
    highlightedNodes,
  ]);

  const handleNodeToolTipShow = (event: cytoscape.EventObject) => {
    const cy = cytoscapeCoreRefObject;
    if (!cy) return;

    if (event.target && event.target.isNode && event.target.isNode()) {
      const node: cytoscape.NodeSingular = event.target;
      const position = event.renderedPosition || node.renderedPosition();

      // xオフセットは、マウスの位置が画面の右半分にある場合は左側に表示し、左半分にある場合は右側に表示
      const canvasWidth = cy.width();
      const xOffset =
        position.x > canvasWidth / 2
          ? -40 /* 左側に表示 */
          : 60; /* 右側に表示 */

      showTooltip(tooltipRef, node.data(), {
        x: position.x + xOffset,
        y: position.y + 10,
      });
    }
  };

  // ノード位置を追跡するための変数を用意
  const dragPositions = new Map<string, { x: number; y: number }>();
  const onDragFree = (event: cytoscape.EventObject) => {
    const cy = cytoscapeCoreRefObject;
    if (!cy) return;

    const node = event.target;
    if (node.data('nodeType') !== NodeType.THEME) return;

    const oldPosition = dragPositions.get(node.id());
    if (!oldPosition) return;
    const newPosition = node.position();
    const dx = newPosition.x - oldPosition.x;
    const dy = newPosition.y - oldPosition.y;

    const themeNodes = cy.nodes(`[nodeType = "${NodeType.THEME}"]`);
    const connectedNodes = node.neighborhood('node').not(themeNodes);

    connectedNodes.forEach((connectedNode: cytoscape.NodeSingular) => {
      const pos = connectedNode.position();
      connectedNode.position({
        x: pos.x + dx,
        y: pos.y + dy,
      });
    });

    node.scratch('_oldPosition', null);
  };

  const onGrab = (event: cytoscape.EventObject) => {
    const node = event.target;
    if (node.data('nodeType') === NodeType.THEME) {
      const pos = node.position();
      dragPositions.set(node.id(), { x: pos.x, y: pos.y });
    }
  };

  // zoom イベントを追加
  const onZoom = () => {
    const cy = cytoscapeCoreRefObject;
    if (!cy) return;

    const currentZoom = Number(cy.zoom().toFixed(2));
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentZoom));
    setZoomLevel(clamped);
  };

  useEffect(() => {
    const cy = cytoscapeCoreRefObject;
    if (!cy) return;

    const container = cy.container();

    // DOM 用の wheel ハンドラ（cytoscape の EventObject を期待する handleWheelRef と別物）
    const onContainerWheel = (e: WheelEvent) => {
      // cy が無ければ何もしない
      if (!cy) return;

      // 自前で処理するため組み込みズームを無効化
      if (typeof cy.userZoomingEnabled === 'function') {
        cy.userZoomingEnabled(false);
      }

      if (e.deltaY === 0 && e.deltaX === 0) return;

      // Ctrl 押下時のみ、または Shift 押下時のみ preventDefault を呼ぶ
      // それ以外（通常のスクロール）は、特定の閾値を超えた場合のみ処理
      if (e.ctrlKey) {
        // ズーム操作
        e.preventDefault();
        const sensitivity = 0.0015;
        const cur = cy.zoom();
        const factor = 1 - e.deltaY * sensitivity;
        const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, cur * factor));

        const rect = container!.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        cy.zoom({ level: next, position: { x: mouseX, y: mouseY } });
        setZoomLevel(Number(next.toFixed(2)));
      } else if (e.shiftKey) {
        // Shift + スクロールで水平パン
        e.preventDefault();
        cy.panBy({ x: -e.deltaY, y: 0 });
      } else {
        // 通常のスクロール: 縦方向が主な場合はパン、横方向が主な場合はブラウザに任せる
        const isMainlyHorizontal =
          Math.abs(e.deltaX) > Math.abs(e.deltaY) * 1.5;
        const isMainlyVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX) * 1.5;

        if (isMainlyVertical) {
          // 縦方向のスクロールはグラフのパンとして処理
          e.preventDefault();
          cy.panBy({ x: -e.deltaX, y: -e.deltaY });
        } else if (isMainlyHorizontal && Math.abs(e.deltaX) > 10) {
          // 横方向のスクロールで、かつ閾値を超えている場合のみパンとして処理
          // 閾値を下回る場合はブラウザのデフォルト動作（ブラウザバック等）を許可
          e.preventDefault();
          cy.panBy({ x: -e.deltaX, y: -e.deltaY });
        }
        // それ以外（小さな横方向の動き）はpreventDefaultせず、ブラウザに任せる
      }
    };

    container?.addEventListener('wheel', onContainerWheel, { passive: false });

    // リスナーを登録
    cy.on('tap', (event) => handleClickRef.current?.(event));
    cy.on('hover', (event) => handleMouseOverRef.current?.(event));
    cy.on('mouseover', (event) => handleMouseOverRef.current?.(event));
    cy.on('mouseout', (event) => handleMouseOutRef.current?.(event));

    cy.on('dragfree', 'node', (event) => onDragFree(event));
    cy.on('grab', 'node', (event) => onGrab(event));
    cy.on('zoom', onZoom);

    // クリーンアップ: イベントリスナーを解除し、参照をクリア
    return () => {
      cy.off('zoom', onZoom);
      cy.off('tap');
      cy.off('mouseover');
      cy.off('mouseout');
      cy.off('wheel');
      container?.removeEventListener('wheel', onContainerWheel);
      cy.off('dragfree', 'node');
      cy.off('grab', 'node');
      removeHighlighting(cy);
      clearSelection();
      dragPositions.clear(); // ドラッグ位置をクリア
    };
  }, [cytoscapeCoreRefObject]);
};
